export type PageSize = 25 | 50 | 100 | 200;

interface Props {
  page: number;
  pageSize: PageSize;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: PageSize) => void;
}

export function Pagination({
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: Props) {
  const safeTotalPages = Math.max(1, totalPages);
  const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);
  const windowStart = Math.max(1, Math.min(safeTotalPages - 4, page - 2));

  return (
    <div className="pagination">
      <div>
        Showing <strong>{start}</strong>–<strong>{end}</strong> of{" "}
        <strong>{totalCount.toLocaleString()}</strong> items
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontSize: 12 }}>Rows per page:</span>
        <select
          aria-label="Rows per page"
          className="gw-select"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value) as PageSize)}
        >
          {[25, 50, 100, 200].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div className="pages">
          <button onClick={() => onPageChange(1)} disabled={page === 1} aria-label="First page">
            «
          </button>
          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Previous page"
          >
            ‹
          </button>
          {Array.from({ length: Math.min(5, safeTotalPages) }, (_, i) => {
            const num = windowStart + i;
            if (num > safeTotalPages) return null;
            return (
              <button
                key={num}
                className={page === num ? "active" : ""}
                onClick={() => onPageChange(num)}
                aria-label={`Page ${num}`}
                aria-current={page === num ? "page" : undefined}
              >
                {num}
              </button>
            );
          })}
          <button
            onClick={() => onPageChange(Math.min(safeTotalPages, page + 1))}
            disabled={page === safeTotalPages}
            aria-label="Next page"
          >
            ›
          </button>
          <button
            onClick={() => onPageChange(safeTotalPages)}
            disabled={page === safeTotalPages}
            aria-label="Last page"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}
